import { setCaseGenerationInProgress } from '../services/gameAPI';

export const GENERATION_KEY = 'casefiles_generation_in_progress';
export const GENERATION_JOB_KEY = 'casefiles_generation_job_id';
export const GENERATION_JOB_STARTED_AT_KEY = 'casefiles_generation_job_started_at';
export const GENERATION_RECOVERY_KEY = 'casefiles_generation_recovery';
export const GENERATION_RECOVERY_TTL_MS = 30 * 60 * 1000;
export const GENERATION_JOB_TTL_MS = GENERATION_RECOVERY_TTL_MS;
export const DEFAULT_GENERATION_FAILURE_MESSAGE = "We couldn't finish building this case file. Please try again.";

const INTERNAL_CATEGORY_PATTERN = /(?:repair[_\s-]*controller[_\s-]*failure|validation[_\s-]*category|step[_\s-]*retryable|schema[_\s-]*path|source[_\s-]*path)/i;
const SCHEMA_PATH_PATTERN = /\b(?:caseData|case_data|initialState|suspects|clues|locations|documents|evidence|timeline)(?:\[[^\]]+\]|\.[A-Za-z0-9_]+)/i;
const INTERNAL_EXCEPTION_PATTERN = /(?:\b(?:[A-Z][A-Za-z0-9_]*Error|[A-Z][A-Za-z0-9_]*Exception|Exception)\s*:|\bValidationError\b|\bPydantic\s+validation\s+error\b|\bTraceback\b)/i;
const INTERNAL_PUBLIC_ID_PATTERN = /(?:repair[_-]*controller[_-]*failure|validation[_-]*category|step[_-]*retryable|schema[_-]*path|source[_-]*path|output[_-]*schema|case[_-]*data|initial[_-]*state|(?:^|[_-])schema(?:$|[_-]))/i;
const PUBLIC_REFERENCE_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

const isRawGenerationFailureMessage = (message) => {
  if (typeof message !== 'string') {
    return false;
  }

  return INTERNAL_CATEGORY_PATTERN.test(message) ||
    SCHEMA_PATH_PATTERN.test(message) ||
    INTERNAL_EXCEPTION_PATTERN.test(message);
};

const isInternalMetadataValue = (value) => (
  INTERNAL_CATEGORY_PATTERN.test(value) ||
  INTERNAL_PUBLIC_ID_PATTERN.test(value) ||
  SCHEMA_PATH_PATTERN.test(value)
);

const safeString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
};

const safePublicId = (value) => {
  const trimmed = safeString(value);
  if (!trimmed) {
    return null;
  }

  if (
    !PUBLIC_REFERENCE_ID_PATTERN.test(trimmed) ||
    isInternalMetadataValue(trimmed)
  ) {
    return null;
  }

  return trimmed;
};

const safeMessage = (recovery) => {
  const message = safeString(recovery?.user_message) || safeString(recovery?.message);
  if (!message || isRawGenerationFailureMessage(message)) {
    return DEFAULT_GENERATION_FAILURE_MESSAGE;
  }

  return message;
};

const safeKind = (kind) => {
  const normalizedKind = safeString(kind);
  if (!normalizedKind || isInternalMetadataValue(normalizedKind)) {
    return 'generation_failed';
  }

  return normalizedKind;
};

const safePublicCategory = (recovery) => {
  const publicCategory = safeString(recovery?.publicCategory) || safeString(recovery?.public_category);
  if (!publicCategory || isInternalMetadataValue(publicCategory)) {
    return null;
  }

  return publicCategory;
};

const normalizeGenerationRecovery = (recovery, { now = Date.now(), expiresAt } = {}) => {
  const jobId = safePublicId(recovery?.jobId) || safePublicId(recovery?.job_id);
  const referenceId = safePublicId(recovery?.referenceId) ||
    safePublicId(recovery?.reference_id) ||
    jobId;
  const createdAt = Number.isFinite(Number(recovery?.createdAt))
    ? Number(recovery.createdAt)
    : now;
  const normalizedExpiresAt = Number.isFinite(Number(expiresAt))
    ? Number(expiresAt)
    : now + GENERATION_RECOVERY_TTL_MS;
  const publicCategory = safePublicCategory(recovery);
  const normalizedRecovery = {
    jobId,
    referenceId,
    message: safeMessage(recovery),
    retryable: recovery?.retryable !== false,
    kind: safeKind(recovery?.kind),
    createdAt,
    expiresAt: normalizedExpiresAt,
  };

  if (publicCategory) {
    normalizedRecovery.publicCategory = publicCategory;
  }

  return normalizedRecovery;
};

const getStorage = () => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage;
};

export const readGenerationRecovery = () => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const storedRecovery = storage.getItem(GENERATION_RECOVERY_KEY);
    if (!storedRecovery) {
      return null;
    }

    const parsedRecovery = JSON.parse(storedRecovery);
    if (!parsedRecovery || typeof parsedRecovery !== 'object') {
      storage.removeItem(GENERATION_RECOVERY_KEY);
      return null;
    }

    const expiresAt = Number(parsedRecovery.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      storage.removeItem(GENERATION_RECOVERY_KEY);
      return null;
    }

    const normalizedRecovery = normalizeGenerationRecovery(parsedRecovery, { expiresAt });
    storage.setItem(GENERATION_RECOVERY_KEY, JSON.stringify(normalizedRecovery));
    return normalizedRecovery;
  } catch (error) {
    console.warn('[generationLifecycle] Ignoring invalid generation recovery state', error);
    storage.removeItem(GENERATION_RECOVERY_KEY);
    return null;
  }
};

export const writeGenerationRecovery = (recovery) => {
  const now = Date.now();
  const normalizedRecovery = normalizeGenerationRecovery(recovery, {
    now,
    expiresAt: now + GENERATION_RECOVERY_TTL_MS,
  });
  const storage = getStorage();
  if (!storage) {
    return normalizedRecovery;
  }

  try {
    storage.setItem(GENERATION_RECOVERY_KEY, JSON.stringify(normalizedRecovery));
  } catch (error) {
    console.warn('[generationLifecycle] Unable to persist generation recovery state', error);
  }

  return normalizedRecovery;
};

export const clearGenerationRecovery = () => {
  const storage = getStorage();
  if (storage) {
    storage.removeItem(GENERATION_RECOVERY_KEY);
  }
};

export const readScopedGenerationRecovery = ({
  activeJobId = null,
  isStartRequestInProgress = false,
} = {}) => {
  const recovery = readGenerationRecovery();
  if (!recovery) {
    return null;
  }

  const safeActiveJobId = safePublicId(activeJobId);
  if (safeActiveJobId) {
    if (recovery.jobId === safeActiveJobId) {
      return recovery;
    }

    clearGenerationRecovery();
    return null;
  }

  if (isStartRequestInProgress) {
    clearGenerationRecovery();
    return null;
  }

  return recovery;
};

export const isGenerationInProgress = () => {
  const storage = getStorage();
  return storage?.getItem(GENERATION_KEY) === 'true';
};

export const setGenerationInProgress = (inProgress) => {
  const storage = getStorage();
  setCaseGenerationInProgress(inProgress);

  if (!storage) {
    return;
  }

  if (inProgress) {
    storage.setItem(GENERATION_KEY, 'true');
  } else {
    storage.removeItem(GENERATION_KEY);
  }
};

export const getGenerationJobId = () => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const jobId = storage.getItem(GENERATION_JOB_KEY);
  const safeJobId = safePublicId(jobId);
  if (!safeJobId && jobId) {
    storage.removeItem(GENERATION_JOB_KEY);
  }

  return safeJobId;
};

export const setGenerationJobId = (jobId) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  const safeJobId = safePublicId(jobId);
  if (safeJobId) {
    storage.setItem(GENERATION_JOB_KEY, safeJobId);
    storage.setItem(GENERATION_JOB_STARTED_AT_KEY, String(Date.now()));
  } else {
    storage.removeItem(GENERATION_JOB_KEY);
    storage.removeItem(GENERATION_JOB_STARTED_AT_KEY);
  }
};

export const clearGenerationSession = () => {
  setGenerationInProgress(false);
  setGenerationJobId(null);
};

export const clearGenerationLifecycle = () => {
  clearGenerationSession();
  clearGenerationRecovery();
};

export const getGenerationJobStartedAt = () => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const startedAt = Number(storage.getItem(GENERATION_JOB_STARTED_AT_KEY));
  return Number.isFinite(startedAt) ? startedAt : null;
};

export const resolveGenerationLifecycle = ({ now = Date.now() } = {}) => {
  const activeJobId = getGenerationJobId();
  const startRequestInProgress = isGenerationInProgress();
  const jobStartedAt = getGenerationJobStartedAt();
  const terminalRecovery = readGenerationRecovery();

  if (activeJobId && startRequestInProgress) {
    if (jobStartedAt && now - jobStartedAt > GENERATION_JOB_TTL_MS) {
      clearGenerationSession();
      return {
        status: 'unknown_maybe_running',
        jobId: activeJobId,
        recovery: null,
      };
    }

    clearGenerationRecovery();
    return {
      status: 'polling',
      jobId: activeJobId,
      recovery: null,
    };
  }

  if (startRequestInProgress && !activeJobId) {
    clearGenerationRecovery();
    return {
      status: 'starting',
      jobId: null,
      recovery: null,
    };
  }

  clearGenerationLifecycle();
  const idleLifecycle = {
    status: 'idle',
    jobId: null,
    recovery: null,
  };

  if (terminalRecovery) {
    idleLifecycle.clearedTerminalRecovery = true;
  }

  return idleLifecycle;
};
